import { usePrevious } from '@moda/portal-stanchions';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useCart } from '../../hooks/useCart';
import { useCookies } from '../../hooks/useCookies';

const JUST_ADDED_DURATION_MS = 4000;

export const useJustAddedItem = () => {
  const [justAddedItemId, setJustAddedItemId] = useState<string | null>(null);
  const [isNewCart, setIsNewCart] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    cookies: { cartId }
  } = useCookies();
  const { cart, loaded } = useCart({ ssr: false });
  const cartItems = useMemo(() => cart.attributes.cartItems.map(item => item.data), [cart]);
  const previousCartItems = usePrevious(cartItems);
  const previousLoaded = usePrevious(loaded);

  const newCartItem = useMemo(
    () =>
      cartItems.find(
        item =>
          !previousCartItems?.find(
            previousItem =>
              previousItem.id === item.id &&
              previousItem.attributes.quantity === item.attributes.quantity
          )
      ),
    [cartItems, previousCartItems]
  );

  useEffect(() => {
    if (!cartId) {
      setIsNewCart(true);
    }
  }, [cartId, setIsNewCart]);

  useEffect(() => {
    if (newCartItem && (previousLoaded || isNewCart)) {
      setJustAddedItemId(newCartItem.id);
      setIsNewCart(false);
    }
  }, [newCartItem, setJustAddedItemId, previousLoaded, isNewCart]);

  useEffect(() => {
    if (justAddedItemId) {
      timeoutRef.current = global.setTimeout(
        () => setJustAddedItemId(null),
        JUST_ADDED_DURATION_MS
      );
      return () => {
        timeoutRef.current && clearTimeout(timeoutRef.current);
      };
    }
  }, [justAddedItemId, setJustAddedItemId, timeoutRef]);

  const justAddedItem = useMemo(
    () => cartItems.find(item => item.id === justAddedItemId) || null,
    [cartItems, justAddedItemId]
  );

  const dismiss = useCallback(() => setJustAddedItemId(null), [setJustAddedItemId]);
  const persist = useCallback(
    () => timeoutRef.current && clearTimeout(timeoutRef.current),
    [timeoutRef]
  );

  return {
    justAddedItem,
    dismiss,
    persist
  };
};
